/**
 * The run's numbers — the middle beat of the death sequence (`TODO.md` §7.1).
 *
 * A roguelike death is a score screen, and this app had the epitaph and the way out with
 * nothing between them: the player was told the character was dead and offered a new story,
 * and never told what the old one had come to. Four numbers close that gap, and every one of
 * them is already on the client — **no contract change bought any of this**, which is why it
 * was the half of §7.1 that could ship without waiting on the API.
 *
 * **Two of them are always known and two are not**, and that asymmetry is the reason this is a
 * function rather than four lines of JSX. Chapters and days come from the story and its newest
 * chapter, both of which are loaded before the feed renders at all. Places and people come
 * from `GET /stories/{id}/state`, which is fetched but never awaited (`PlayRoute`) and can
 * fail on its own — so the block has to be able to render three rows, or two. It renders what
 * it knows and omits the rest; a dash where a number should be is the interface admitting to a
 * failed request in the middle of the most deliberate moment in the game.
 *
 * **`days` is the in-game clock, not wall time.** The character who died on day 6 survived six
 * days of hunger and thirst, however many evenings the player spent getting them there — which
 * is the number the mechanics are denominated in (`rules.py`: thirst kills in ~48h).
 */
import type { Chapter, WorldSnapshot } from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';

/** One row: a label key and the number under it. */
export interface RunStat {
  key: UiKey;
  value: number;
}

interface RunStatsInput {
  /** `story.chapter_count` — the whole story, not the pages the feed happens to have loaded. */
  chapterCount: number;
  /** The last chapter written. Its `world_time` is where the clock stopped. */
  last: Pick<Chapter, 'world_time'> | undefined;
  /** Null while `/state` is pending or failed — the two counts below simply do not appear. */
  world: WorldSnapshot | null;
}

/**
 * The rows to draw, in reading order, skipping any the client cannot answer for.
 *
 * `world.locations` rather than `character.known_locations`: the codex's own count is what the
 * player can go and look at behind the same drawer, and a "places found" that disagreed with
 * the number beside *places* one panel over would be the more confusing of the two truths.
 */
export function runStats({ chapterCount, last, world }: RunStatsInput): RunStat[] {
  const stats: RunStat[] = [{ key: 'ui.death.stat.chapters', value: chapterCount }];

  if (last !== undefined) {
    stats.push({ key: 'ui.death.stat.days', value: last.world_time.day });
  }

  if (world !== null) {
    stats.push({ key: 'ui.death.stat.places', value: world.locations.length });
    stats.push({ key: 'ui.death.stat.people', value: world.npcs.length });
  }

  return stats;
}
